import React, { useEffect, useState } from 'react';
import { Layers, Loader2, X, Eye, EyeOff, Trash2, Percent, Download, Upload, Car, Sparkles, Boxes, Fuel } from 'lucide-react';
import { BulkFuelDialog } from './BulkFuelDialog';
import { toast } from 'sonner';
import { Button } from './ui/button';
import SubcategorySelect from './SubcategorySelect';
import { adminAPI } from '../services/api';
import { BulkPriceDialog } from './BulkPriceDialog';
import { BulkStockDialog } from './BulkStockDialog';
import { CompatPasteModal } from './CompatPasteModal';
import { CompatAiJobModal } from './CompatAiJobModal';

const selectCls = 'border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-400';
const ghostBtn = 'border-gray-500 text-gray-100 hover:bg-gray-800 bg-transparent';

export const BulkCategoriesBar = ({ categories = [], selectedIds, selectedProducts = [], onClear, onDone, onImport }) => {
  const [priceOpen, setPriceOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [compatOpen, setCompatOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [fuelOpen, setFuelOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => { if (!category && categories.length) setCategory(categories[0].slug); }, [categories, category]);

  const n = selectedIds.length;

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const r = await fn();
      const skipped = r.skipped?.length ? ` · ${r.skipped.length} ignoré(s) (${[...new Set(r.skipped.map(s => s.reason))].join(', ')})` : '';
      toast.success(`${r.message}${skipped}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opération impossible');
    } finally {
      setBusy('');
    }
  };
  const runCategories = (action) => run(action, () => adminAPI.bulkCategories(selectedIds, category, subcategory, action));
  const exportCsv = async () => {
    setBusy('export');
    try { await adminAPI.exportSelection(selectedIds); toast.success(`${n} produit(s) exporté(s) en CSV`); }
    catch { toast.error('Export impossible'); }
    finally { setBusy(''); }
  };
  const runAction = (action) => {
    if (action === 'delete' && !window.confirm(`Supprimer définitivement ${n} produit(s) ? Cette action est irréversible.`)) return;
    run(action, () => adminAPI.bulkAction(selectedIds, action));
  };

  if (!n) return null;
  return (
    <div className="sticky top-2 z-20 bg-gray-900 text-white rounded-lg shadow-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-3" data-testid="products-bulk-bar">
      <span className="inline-flex items-center gap-2 text-sm font-semibold" data-testid="products-bulk-count"><Layers size={16} className="text-orange-400" /> {n} produit(s) sélectionné(s)</span>
      <span className="text-xs text-gray-300">Catégorie supplémentaire :</span>
      <select data-testid="products-bulk-category" value={category} onChange={e => { setCategory(e.target.value); setSubcategory(''); }} className={selectCls}>
        {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
      </select>
      <SubcategorySelect testId="products-bulk-subcategory" category={categories.find(c => c.slug === category)} value={subcategory} onChange={e => setSubcategory(e.target.value)} className={selectCls} emptyLabel="Toute la catégorie" disabledLabel="Aucune sous-catégorie" />
      <Button type="button" size="sm" data-testid="products-bulk-add" disabled={!!busy || !category} onClick={() => runCategories('add')} className="bg-orange-500 hover:bg-orange-600 text-white">
        {busy === 'add' && <Loader2 size={14} className="mr-1 animate-spin" />} Ajouter
      </Button>
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-remove" disabled={!!busy || !category} onClick={() => runCategories('remove')} className={ghostBtn}>
        {busy === 'remove' && <Loader2 size={14} className="mr-1 animate-spin" />} Retirer
      </Button>
      <span className="hidden sm:block w-px h-6 bg-gray-700" />
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-price" disabled={!!busy} onClick={() => setPriceOpen(true)} className={ghostBtn} title="Remise ou hausse en pourcentage">
        <Percent size={14} className="mr-1" /> Prix
      </Button>
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-stock" disabled={!!busy} onClick={() => setStockOpen(true)} className={ghostBtn} title="Définir, ajouter ou retirer du stock entrepôt">
        <Boxes size={14} className="mr-1" /> Stock
      </Button>
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-compat" disabled={!!busy} onClick={() => setCompatOpen(true)} className={ghostBtn} title="Coller une liste de véhicules compatibles sur tous les produits sélectionnés">
        <Car size={14} className="mr-1" /> Véhicules
      </Button>
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-ai-compat" disabled={!!busy} onClick={() => setAiOpen(true)} className="border-violet-400 text-violet-200 hover:bg-violet-900/40 bg-transparent" title="L'IA propose les véhicules compatibles (référence + numéros OE) ; seuls ceux retrouvés dans la base sont ajoutés">
        <Sparkles size={14} className="mr-1" /> IA compat.
      </Button>
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-export" disabled={!!busy} onClick={exportCsv} className={ghostBtn} title="Télécharger la sélection en CSV">
        {busy === 'export' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} CSV
      </Button>
      {onImport && (
        <Button type="button" size="sm" variant="outline" data-testid="products-bulk-import" disabled={!!busy} onClick={onImport} className={ghostBtn} title="Réimporter un CSV modifié dans un tableur (mise à jour par référence)">
          <Upload size={14} className="mr-1" /> Importer CSV
        </Button>
      )}
      <span className="hidden sm:block w-px h-6 bg-gray-700" />
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-publish" disabled={!!busy} onClick={() => runAction('publish')} className={ghostBtn} title="Rendre visibles sur le site">
        {busy === 'publish' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />} Publier
      </Button>
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-unpublish" disabled={!!busy} onClick={() => runAction('unpublish')} className={ghostBtn} title="Masquer du site (conservés dans l'admin)">
        {busy === 'unpublish' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <EyeOff size={14} className="mr-1" />} Dépublier
      </Button>
      <Button type="button" size="sm" data-testid="products-bulk-delete" disabled={!!busy} onClick={() => runAction('delete')} className="bg-red-600 hover:bg-red-700 text-white">
        {busy === 'delete' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />} Supprimer
      </Button>
      <Button type="button" size="sm" variant="outline" data-testid="products-bulk-fuel" disabled={!!busy} onClick={() => setFuelOpen(true)} className={ghostBtn} title="Restriction d'énergie : Essence / Diesel / 100 % électrique">
        <Fuel size={14} className="mr-1" /> Énergie
      </Button>
      <button type="button" data-testid="products-bulk-clear" onClick={onClear} className="ml-auto inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white"><X size={14} /> Désélectionner</button>
      <CompatPasteModal open={compatOpen} onClose={() => setCompatOpen(false)} bulkCount={n} onApply={async (result, mode, text) => {
        const r = await adminAPI.bulkCompat(selectedIds, text, mode);
        toast.success(r.message);
        onDone?.();
      }} />
      <CompatAiJobModal open={aiOpen} onClose={() => setAiOpen(false)} productIds={selectedIds} onDone={onDone} />
      <BulkFuelDialog open={fuelOpen} onClose={() => setFuelOpen(false)} selectedIds={selectedIds} onDone={onDone} />
      <BulkPriceDialog open={priceOpen} onClose={() => setPriceOpen(false)} selectedIds={selectedIds} sampleProducts={selectedProducts} onDone={onDone} />
      <BulkStockDialog open={stockOpen} onClose={() => setStockOpen(false)} selectedIds={selectedIds} sampleProducts={selectedProducts} onDone={onDone} />
    </div>
  );
};
